/* eslint-disable react/require-default-props */
import React from 'react';
import PropTypes from 'prop-types';

import { FINANCE_ERROR_MESSAGES } from '../../utils/apiError';
import { BlockError } from '../styles';
import { SkeletonBar, SkeletonStack } from '../../../../styles/feedback/Skeleton';
import { SmallButton } from '../../../../styles/ui/Button';

// ---------------------------------------------------------------------------
// Estados de carregamento e erro de UM bloco.
//
// A tela não tem spinner de página: cada dobra carrega e falha sozinha, senão o
// endpoint mais lento (ou o único que caiu) sequestra tudo que já estava pronto.
//
// No erro, 5xx mostra o `error_id` como CÓDIGO DE SUPORTE — é o que permite
// achar o evento no log do backend quando o operador liga reclamando.
// ---------------------------------------------------------------------------

export function BlockSkeleton({ rows, height, gap }) {
   return (
      <SkeletonStack $gap={gap} aria-label="Carregando">
         {Array.from({ length: rows ?? 4 }).map((_, index) => (
            // eslint-disable-next-line react/no-array-index-key
            <SkeletonBar key={index} $h={height ?? 44} />
         ))}
      </SkeletonStack>
   );
}

BlockSkeleton.propTypes = {
   rows: PropTypes.number,
   height: PropTypes.number,
   gap: PropTypes.number,
};

export function BlockErrorState({ error, onRetry }) {
   // 422 que aponta um CAMPO do filtro não é falha deste bloco: é o recorte que
   // está inválido, e o recorte é o mesmo nos 5 endpoints — então os 5 caem
   // juntos. A crítica de verdade já está inline no controle que a causou (a
   // barra de filtros); repetir aqui a mensagem crua do param, cinco vezes,
   // seria ruído. Aqui o bloco só aponta para cima.
   //
   // Sem botão de repetir, de propósito: tentar de novo com o mesmo filtro
   // inválido falha igual — o caminho de saída é corrigir o filtro.
   const hasFieldError = Object.keys(error?.fieldErrors ?? {}).length > 0;

   if (hasFieldError) {
      return (
         <BlockError role="alert">
            <span>Revise o filtro destacado na barra acima.</span>
         </BlockError>
      );
   }

   const message =
      FINANCE_ERROR_MESSAGES[error?.code] ??
      error?.message ??
      'Não foi possível carregar este bloco.';

   return (
      <BlockError role="alert">
         <span>{message}</span>
         {error?.status >= 500 ? (
            <span>Código de suporte: {error?.errorId ?? '—'}</span>
         ) : null}
         {onRetry ? (
            <SmallButton type="button" onClick={onRetry}>
               Tentar novamente
            </SmallButton>
         ) : null}
      </BlockError>
   );
}

BlockErrorState.propTypes = {
   error: PropTypes.shape({
      code: PropTypes.string,
      message: PropTypes.string,
      status: PropTypes.number,
      errorId: PropTypes.string,
      fieldErrors: PropTypes.objectOf(PropTypes.string),
   }),
   onRetry: PropTypes.func,
};
